import { zodResolver } from "@hookform/resolvers/zod";
import { BankIcon, CheckCircleIcon, DeviceMobileIcon, LockKeyIcon } from "@phosphor-icons/react";
import { cn, convertFromFcfa, convertToFcfa, formatMoneyValue } from "@reactive-resume/utils";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { useAccountCurrency } from "@/client/services/currency/currency";

import { type WithdrawalRequest, withdrawalRequestSchema } from "../types/wallet";

const MIN_WITHDRAWAL_FCFA = 5000;

interface WithdrawalFormProps {
  balance: number;
  isWithdrawing: boolean;
  onSubmit: (data: WithdrawalRequest) => void | Promise<unknown>;
}

export const WithdrawalForm = ({ balance, isWithdrawing, onSubmit }: WithdrawalFormProps) => {
  const { context: money } = useAccountCurrency();

  // Bornes arrondies pour que le montant converti reste entre 5 000 FCFA et le solde.
  const minAmount = money ? convertFromFcfa(MIN_WITHDRAWAL_FCFA, money, "up") : 0;
  const maxAmount = money ? convertFromFcfa(balance, money, "down") : 0;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    reset,
  } = useForm<WithdrawalRequest>({
    resolver: zodResolver(withdrawalRequestSchema),
    defaultValues: {
      amount: minAmount,
      mean: "MOBILE_MONEY",
      phoneMomo: "",
      bankNumber: "",
    },
  });

  const selectedMean = watch("mean");
  const amount = watch("amount");

  useEffect(() => {
    setValue("amount", minAmount);
  }, [minAmount, setValue]);

  if (!money) {
    return (
      <div
        aria-busy="true"
        className="h-80 animate-pulse rounded-2xl border border-indigo-100 bg-white"
      />
    );
  }

  const submit = async (data: WithdrawalRequest) => {
    await onSubmit({ ...data, amount: convertToFcfa(data.amount, money) });

    reset({
      amount: minAmount,
      mean: data.mean,
      phoneMomo: "",
      bankNumber: "",
    });
  };

  const amountIsInvalid = !Number.isFinite(amount) || amount < minAmount || amount > maxAmount;

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="flex flex-col gap-6 rounded-2xl border border-indigo-100 bg-white p-5 shadow-[0_10px_30px_rgba(30,27,75,0.06)] sm:p-6"
    >
      <div>
        <div className="mb-3 flex size-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
          <CheckCircleIcon size={20} weight="duotone" />
        </div>

        <h3 className="text-base font-bold text-[#071451]">Demander un retrait</h3>

        <p className="mt-1 text-sm leading-5 text-slate-500">
          Transférez vos gains vers votre compte bancaire ou Mobile Money.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="amount" className="mb-1.5 block text-sm font-medium text-slate-700">
            Montant à retirer
          </label>

          <div className="relative">
            <input
              id="amount"
              type="number"
              min={minAmount}
              max={maxAmount}
              step="any"
              inputMode="decimal"
              className={cn(
                "block h-11 w-full rounded-xl border border-slate-300 bg-white px-4 pr-16 text-slate-900 placeholder-slate-400 transition-colors",
                "focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100",
                errors.amount && "border-red-300 focus:border-red-500 focus:ring-red-100",
              )}
              placeholder={String(minAmount)}
              {...register("amount", {
                valueAsNumber: true,
              })}
            />

            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
              <span className="text-sm font-medium text-slate-500">{money.currency}</span>
            </div>
          </div>

          {errors.amount ? (
            <p className="mt-1.5 text-xs text-red-500">{errors.amount.message}</p>
          ) : (
            <p className="mt-1.5 text-xs leading-5 text-slate-500">
              Minimum : {formatMoneyValue(minAmount, money)}. Solde disponible :{" "}
              {formatMoneyValue(maxAmount, money)}.
            </p>
          )}
        </div>

        <div>
          <p className="mb-1.5 text-sm font-medium text-slate-700">Méthode de retrait</p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() =>
                setValue("mean", "MOBILE_MONEY", {
                  shouldValidate: true,
                })
              }
              aria-pressed={selectedMean === "MOBILE_MONEY"}
              className={cn(
                "flex min-h-16 items-center gap-3 rounded-xl border p-4 text-left transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                selectedMean === "MOBILE_MONEY"
                  ? "border-indigo-600 bg-indigo-50 text-indigo-800 shadow-sm ring-2 ring-indigo-100"
                  : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/40",
              )}
            >
              <DeviceMobileIcon
                size={24}
                weight={selectedMean === "MOBILE_MONEY" ? "fill" : "regular"}
              />

              <span className="font-medium">Mobile Money</span>
            </button>

            <button
              type="button"
              onClick={() =>
                setValue("mean", "BANK_TRANSACTION", {
                  shouldValidate: true,
                })
              }
              aria-pressed={selectedMean === "BANK_TRANSACTION"}
              className={cn(
                "flex min-h-16 items-center gap-3 rounded-xl border p-4 text-left transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                selectedMean === "BANK_TRANSACTION"
                  ? "border-indigo-600 bg-indigo-50 text-indigo-800 shadow-sm ring-2 ring-indigo-100"
                  : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/40",
              )}
            >
              <BankIcon
                size={24}
                weight={selectedMean === "BANK_TRANSACTION" ? "fill" : "regular"}
              />

              <span className="font-medium">Virement</span>
            </button>
          </div>
        </div>

        {selectedMean === "MOBILE_MONEY" && (
          <div className="animate-in fade-in slide-in-from-top-2">
            <label htmlFor="phoneMomo" className="mb-1.5 block text-sm font-medium text-slate-700">
              Numéro Mobile Money
            </label>

            <input
              id="phoneMomo"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              className={cn(
                "block h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-slate-900 placeholder-slate-400 transition-colors",
                "focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100",
                errors.phoneMomo && "border-red-300 focus:border-red-500 focus:ring-red-100",
              )}
              placeholder="Ex. 6XXXXXXXX"
              {...register("phoneMomo")}
            />

            {errors.phoneMomo && (
              <p className="mt-1.5 text-xs text-red-500">{errors.phoneMomo.message}</p>
            )}
          </div>
        )}

        {selectedMean === "BANK_TRANSACTION" && (
          <div className="animate-in fade-in slide-in-from-top-2">
            <label htmlFor="bankNumber" className="mb-1.5 block text-sm font-medium text-slate-700">
              IBAN ou numéro de compte
            </label>

            <input
              id="bankNumber"
              type="text"
              autoComplete="off"
              className={cn(
                "block h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-slate-900 placeholder-slate-400 transition-colors",
                "focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100",
                errors.bankNumber && "border-red-300 focus:border-red-500 focus:ring-red-100",
              )}
              placeholder="Ex. CM21 1000..."
              {...register("bankNumber")}
            />

            {errors.bankNumber && (
              <p className="mt-1.5 text-xs text-red-500">{errors.bankNumber.message}</p>
            )}
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={isWithdrawing || amountIsInvalid}
        className={cn(
          "mt-2 flex min-h-12 w-full items-center justify-center rounded-xl px-4 text-sm font-semibold text-white transition-all",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2",
          isWithdrawing || amountIsInvalid
            ? "cursor-not-allowed bg-slate-300"
            : "bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 shadow-md hover:-translate-y-0.5",
        )}
      >
        {isWithdrawing
          ? "Traitement en cours..."
          : amount > maxAmount
            ? "Solde insuffisant"
            : amount < minAmount
              ? `Minimum ${formatMoneyValue(minAmount, money)}`
              : "Confirmer le retrait"}
      </button>

      <p className="flex items-start gap-2 text-[10px] leading-4 text-slate-500">
        <LockKeyIcon size={14} className="mt-0.5 shrink-0 text-indigo-500" />
        Votre demande est vérifiée avant transfert. Aucun débit n’est effectué si elle est rejetée.
      </p>
    </form>
  );
};
